import { useTracking } from '@components/hooks';
import { validateContactFormFields } from '@components/utils';
import api from '@components/api';
import styles from '@components/components/CmsComponents/components/CmsContactForm/components/CustomForm/CustomForm.module.scss';
import Step1 from '@components/components/CmsComponents/components/CmsContactForm/components/Step1/Step1';
import Step2 from '@components/components/CmsComponents/components/CmsContactForm/components/Step2/Step2';
import { Card, CardBody } from '@wienenergiegithub/ui-next';
import { useTranslation } from '@wienenergiegithub/modules-next/hooks';
import React, { useCallback, useEffect, useState } from 'react';
import { useFormStore, Form } from '@ariakit/react';

const CustomForm = ({ contact, headingLevel = 2 }) => {
  const [step, setStep] = useState(1);
  const { t } = useTranslation('cmscontactform');
  const track = useTracking();

  useEffect(() => {
    if (step === 2) {
      window.location.hash = 'customFormSuccess';
    }
  }, [step]);

  const getDefaultValues = useCallback(
    () =>
      contact.fields.reduce((values, field) => {
        if (field.type === 'salutation') {
          values[field.name] = t('female');
        } else if (field.type === 'checkboxGroup') {
          values[field.name] = [];
        } else {
          values[field.name] = '';
        }
        return values;
      }, {}),
    [contact],
  );

  const getUploadField = useCallback(
    () => contact.fields.find(field => field.type === 'upload'),
    [contact],
  );

  const [values, setValues] = useState({ ...getDefaultValues(), email: '' });

  const form = useFormStore({ values, setValues });

  form.useValidate(() => {
    validateContactFormFields(form, contact, t, values);
  });

  form.useSubmit(async () => {
    const { formId, template } = contact;
    // delete honeypot field if it is empty
    if (values.email === '') {
      delete values.email;
    }
    const uploadField = getUploadField();

    const formData = new FormData();
    formData.append('formId', formId);
    formData.append('template', template);

    if (uploadField) {
      const upload = values[uploadField.name];

      if (upload && upload?.length !== 0) {
        upload.forEach((file, i) => {
          formData.append(`file-${i}`, file, file.name);
        });
      }
    }

    formData.append('values', JSON.stringify(values));

    try {
      const response = await api.post('/contactform/custom', formData);
      if (response.data?.success) {
        // Add useTracking events just for certain custom contact forms
        switch (contact.emailTemplate) {
          case 'weChargeEasy':
            track({
              event: 'productordertanke_abgeschlossen',
              options: 'zuhause',
              customer: 'privat',
              PLZ: values['field-11'],
            });
            break;
          default:
            track({
              event: 'Formular',
              eventCategory: contact.internalSubject,
              eventAction: 'abgeschickt',
            });
            break;
        }
        setStep(2);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('CustomForm submission error.', error);
    }
  });

  return (
    <div className={styles.container} id="customFormSuccess">
      <Form data-purpose="contact-form" store={form}>
        <Card className={styles.card}>
          <CardBody>
            {step === 1 && <Step1 contact={contact} store={form} />}
            {step === 2 && (
              <Step2 contact={contact} headingLevel={headingLevel} />
            )}
          </CardBody>
        </Card>
      </Form>
    </div>
  );
};

export default CustomForm;
